import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #17a2b8;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #e0f7fa, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #17a2b8;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const PromptsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
`;

const PromptItem = styled.div`
  background: #f0f8ff;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #b3e5fc;
  position: relative;
`;

const PromptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const PromptLabel = styled.div`
  font-weight: 600;
  color: #0277bd;
  font-size: 14px;
`;

const DeleteButton = styled(Button)`
  padding: 4px 10px !important;
  font-size: 12px !important;
`;

const AddButton = styled(Button)`
  margin-top: 10px;
  font-size: 13px;
`;

const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const AnswerItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AnswerBadge = styled.span`
  background: #4caf50;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  min-width: 25px;
  text-align: center;
  font-size: 11px;
`;

const SmallDeleteButton = styled(Button)`
  padding: 2px 8px !important;
  font-size: 11px !important;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 15px;
  padding: 10px;
  background: #fff3cd;
  border-radius: 4px;
  border: 1px solid #ffc107;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    margin: 0;
    cursor: pointer;
    font-size: 14px;
    color: #856404;
  }
`;

interface WriteSentencePromptsFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const WriteSentencePromptsForm: React.FC<WriteSentencePromptsFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : null;
      
      if (parsed && parsed.prompts) {
        // Convert old format (answer: string) to new format (answers: string[])
        const prompts = parsed.prompts.map((p: any) => {
          if (p.answers && Array.isArray(p.answers)) {
            return p;
          } else if (p.answer) {
            return { ...p, answers: [p.answer] };
          } else {
            return { ...p, answers: [''] };
          }
        });
        
        return {
          prompts,
          case_sensitive: parsed.case_sensitive || false,
        };
      }
      
      return {
        prompts: [{ text: '', answers: [''] }],
        case_sensitive: false,
      };
    } catch {
      return {
        prompts: [{ text: '', answers: [''] }],
        case_sensitive: false,
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handlePromptTextChange = (index: number, value: string) => {
    const newPrompts = [...(contentData.prompts || [])];
    newPrompts[index] = {
      ...newPrompts[index],
      text: value,
    };
    setContentData({
      ...contentData,
      prompts: newPrompts,
    });
  };

  const handleAnswerChange = (promptIndex: number, answerIndex: number, value: string) => {
    const newPrompts = [...(contentData.prompts || [])];
    const newAnswers = [...(newPrompts[promptIndex].answers || [''])];
    newAnswers[answerIndex] = value;
    newPrompts[promptIndex] = {
      ...newPrompts[promptIndex],
      answers: newAnswers,
    };
    setContentData({
      ...contentData,
      prompts: newPrompts,
    });
  };

  const handleAddAnswer = (promptIndex: number) => {
    const newPrompts = [...(contentData.prompts || [])];
    newPrompts[promptIndex] = {
      ...newPrompts[promptIndex],
      answers: [...(newPrompts[promptIndex].answers || ['']), ''],
    };
    setContentData({
      ...contentData,
      prompts: newPrompts,
    });
  };

  const handleRemoveAnswer = (promptIndex: number, answerIndex: number) => {
    const newPrompts = [...(contentData.prompts || [])];
    const currentAnswers = newPrompts[promptIndex].answers || [''];
    
    if (currentAnswers.length <= 1) {
      alert('Phải có ít nhất 1 đáp án!');
      return;
    }
    
    newPrompts[promptIndex] = {
      ...newPrompts[promptIndex],
      answers: currentAnswers.filter((_: any, i: number) => i !== answerIndex),
    };
    setContentData({
      ...contentData,
      prompts: newPrompts,
    });
  };

  const handleAddPrompt = () => {
    setContentData({
      ...contentData,
      prompts: [...(contentData.prompts || []), { text: '', answers: [''] }],
    });
  };

  const handleRemovePrompt = (index: number) => {
    const newPrompts = (contentData.prompts || []).filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      prompts: newPrompts.length > 0 ? newPrompts : [{ text: '', answers: [''] }],
    });
  };

  const handleCaseSensitiveChange = (checked: boolean) => {
    setContentData({
      ...contentData,
      case_sensitive: checked,
    });
  };

  return (
    <FormBox>
      <h5>✍️ Nội dung câu hỏi - Viết câu từ các từ gợi ý</h5>
      
      <PromptsList>
        {(contentData.prompts || []).map((prompt: any, index: number) => (
          <PromptItem key={index}>
            <PromptHeader>
              <PromptLabel>Câu {index + 1}</PromptLabel>
              {(contentData.prompts || []).length > 1 && (
                <DeleteButton
                  color="danger"
                  size="sm"
                  onClick={() => handleRemovePrompt(index)}
                >
                  Xóa
                </DeleteButton>
              )}
            </PromptHeader>
            
            <FormGroup style={{ marginBottom: '8px' }}>
              <Label style={{ fontSize: '13px' }}>Các từ gợi ý: *</Label>
              <Input
                type="text"
                value={prompt.text || ''}
                onChange={(e) => handlePromptTextChange(index, e.target.value)}
                placeholder="Ví dụ: They / go / school / bus"
              />
              <small className="form-text text-muted">
                Sử dụng dấu "/" để phân cách các từ
              </small>
            </FormGroup>

            <FormGroup style={{ marginBottom: '0' }}>
              <Label style={{ fontSize: '13px' }}>Các đáp án đúng: *</Label>
              <AnswersList>
                {(prompt.answers || ['']).map((answer: string, answerIdx: number) => (
                  <AnswerItem key={answerIdx}>
                    <AnswerBadge>{answerIdx + 1}</AnswerBadge>
                    <Input
                      type="text"
                      value={answer}
                      onChange={(e) => handleAnswerChange(index, answerIdx, e.target.value)}
                      placeholder={`Đáp án ${answerIdx + 1}...`}
                      style={{ flex: 1 }}
                    />
                    <SmallDeleteButton
                      color="danger"
                      size="sm"
                      onClick={() => handleRemoveAnswer(index, answerIdx)}
                      disabled={(prompt.answers || ['']).length <= 1}
                    >
                      ✕
                    </SmallDeleteButton>
                  </AnswerItem>
                ))}
              </AnswersList>
              <Button
                color="success"
                size="sm"
                onClick={() => handleAddAnswer(index)}
                style={{ marginTop: '8px', fontSize: '12px', padding: '4px 10px' }}
              >
                + Thêm đáp án
              </Button>
              <small className="form-text text-muted" style={{ marginTop: '8px' }}>
                💡 Có thể có nhiều cách viết câu đúng khác nhau
              </small>
            </FormGroup>
          </PromptItem>
        ))}
      </PromptsList>

      <AddButton color="info" size="sm" onClick={handleAddPrompt}>
        + Thêm câu mới
      </AddButton>

      <CheckboxContainer>
        <input
          type="checkbox"
          id="case_sensitive"
          checked={contentData.case_sensitive || false}
          onChange={(e) => handleCaseSensitiveChange(e.target.checked)}
        />
        <label htmlFor="case_sensitive">
          ⚠️ Phân biệt chữ hoa/thường (case-sensitive)
        </label>
      </CheckboxContainer>

      {/* Preview */}
      <div style={{ 
        background: '#e0f2f1', 
        padding: '12px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#00695c' }}>📋 Xem trước:</strong>
        <div style={{ marginTop: '8px', fontSize: '14px' }}>
          {(contentData.prompts || []).map((prompt: any, i: number) => (
            <div key={i} style={{ marginBottom: '12px' }}>
              <div>
                <em>Câu {i + 1}:</em> {prompt.text || '(chưa nhập)'}
              </div>
              <div>
                <em>Các đáp án đúng:</em>
                <ul style={{ margin: '4px 0 0 20px', paddingLeft: 0 }}>
                  {(prompt.answers || ['']).filter((a: string) => a.trim()).map((ans: string, idx: number) => (
                    <li key={idx}><strong>{ans}</strong></li>
                  ))}
                  {(prompt.answers || ['']).filter((a: string) => a.trim()).length === 0 && (
                    <li><strong>(chưa nhập)</strong></li>
                  )}
                </ul>
              </div>
            </div>
          ))}
          {contentData.case_sensitive && (
            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '8px' }}>
              ⚠️ Phân biệt chữ hoa/thường
            </div>
          )}
        </div>
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default WriteSentencePromptsForm;
